import { isAfter } from 'date-fns/isAfter';
import { parseISO } from 'date-fns/parseISO';
import { checkIfProvidedValueMatchesExpectedValue } from '../../adapters/fee-predicate/utils.js';
import { isValidPositiveNumber } from '../../utils/isValidPositiveNumber.js';
import { AfriexFeatureField, AfriexFeatureFrequency, BusinessFeeComparator, } from '../types/index.js';
export const RANGE_INCOMPLETE_MESSAGE = 'Please enter both range values.';
export const RANGE_REVERSED_MESSAGE = 'The lower value must not be greater than the upper value.';
// The fields a range can be set on, named as their error messages name them.
const RANGE_FIELD_LABELS = {
    [AfriexFeatureField.SignupDate]: 'signup date',
    [AfriexFeatureField.TransactionCount]: 'transaction count',
    [AfriexFeatureField.TransactionVolume]: 'transaction volume',
};
// Number('') and Number(' ') are 0, so a blank bound is refused first.
const isValidNumericBound = (bound) => bound.trim() !== '' && isValidPositiveNumber(bound);
/**
 * Why a rule value cannot be matched by `checkTagMatch` below, or undefined
 * when it can. Shared by the admin (on Add and on save) and the server's
 * FeatureRequestValidator, which check each field's own value format
 * themselves. The field also takes its enum values, because admin declares its
 * own AfriexFeatureField.
 */
export const getFeatureRuleValueError = (field, comparator, value) => {
    const label = RANGE_FIELD_LABELS[field];
    if (!label) {
        return undefined;
    }
    const invalidValue = `Invalid ${label} provided for feature tag: ${value}`;
    const isBetween = comparator === BusinessFeeComparator.Between;
    const bounds = value.split(',');
    if (isBetween &&
        (bounds.length < 2 ||
            (bounds.length === 2 && bounds.some((bound) => bound.trim() === '')))) {
        return RANGE_INCOMPLETE_MESSAGE;
    }
    if (field === AfriexFeatureField.SignupDate) {
        // Read as the matcher reads them: parseISO refuses dates that new Date()
        // (the validators' ValidIsoDateTime) accepts, such as 2026/03/31.
        const dates = bounds.map((bound) => parseISO(bound.trim()));
        if (dates.some((date) => isNaN(date.getTime()))) {
            return invalidValue;
        }
        if (!isBetween) {
            return undefined;
        }
        if (bounds.length !== 2) {
            return invalidValue;
        }
        const [lower, upper] = dates;
        return isAfter(lower, upper) ? RANGE_REVERSED_MESSAGE : undefined;
    }
    if (!isBetween) {
        return isValidNumericBound(value) ? undefined : invalidValue;
    }
    if (bounds.length !== 2 || !bounds.every(isValidNumericBound)) {
        return invalidValue;
    }
    const [lower, upper] = bounds.map(Number);
    return lower > upper ? RANGE_REVERSED_MESSAGE : undefined;
};
export class FeatureValidator {
    constructor(feature, tags) {
        this.feature = feature;
        this.tags = tags;
    }
    checkTagMatch(field, expectedValue, actualValue) {
        const [operator, value] = expectedValue?.split('::')?.map((v) => v.trim()) || [];
        if (!value || !operator) {
            return false;
        }
        const isDate = field === AfriexFeatureField.SignupDate;
        const isNumeric = field === AfriexFeatureField.TransactionCount ||
            field === AfriexFeatureField.TransactionVolume;
        return checkIfProvidedValueMatchesExpectedValue({
            operator: operator?.toUpperCase(),
            providedFieldValue: actualValue,
            expectedFieldValue: value,
            isDate,
            isNumeric,
        });
    }
    isFeatureStillAvailableBasedOnStats() {
        const limitValue = Number(this.feature?.meta?.limit?.value || 0);
        if (limitValue === 0) {
            return true;
        }
        const limitFrequency = this.feature?.meta?.limit?.frequency || AfriexFeatureFrequency.Daily;
        if (limitFrequency === AfriexFeatureFrequency.Off) {
            return true;
        }
        const limitType = this.feature?.meta?.limit?.type || 'COUNT';
        const featureLimitStat = Number(this.feature.meta?.stats?.[limitFrequency] || 0);
        if (featureLimitStat === 0) {
            return true;
        }
        const statValue = limitType === 'PERCENTAGE'
            ? Math.floor((featureLimitStat * 100) / limitValue)
            : featureLimitStat;
        return statValue < limitValue;
    }
    value() {
        if (!this.feature) {
            return false;
        }
        if (this.feature.meta?.flags?.shouldIgnoreFlags) {
            return true;
        }
        if (this.feature.meta?.flags?.isDeactivated) {
            return true;
        }
        const featureTags = this.feature.meta?.tags ?? {};
        const tagKeys = Object.keys(featureTags);
        if (tagKeys.length === 0) {
            return true;
        }
        const isFeatureStillAvailable = this.isFeatureStillAvailableBasedOnStats();
        if (!isFeatureStillAvailable) {
            return false;
        }
        // Return true if ANY tag matches (OR logic)
        return tagKeys.some((tag) => this.checkTagMatch(
        // NOTE: the field argument is upper-cased but both lookups below use the
        // ORIGINAL key. That asymmetry is deliberate here only in the sense that
        // it is pre-existing: `tagKeys` come from stored `meta.tags`, so changing
        // the lookup key would change which tag is read at runtime. Typed to
        // match the declared record without altering the key. Worth a separate
        // look at whether the two should agree.
        tag?.toUpperCase(), featureTags?.[tag] || '', this.tags?.[tag] || ''));
    }
}
//# sourceMappingURL=FeatureValidator.js.map